import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

/** Global admin search — ART numbers, titles, slugs, students, lessons,
 * library, media, lexicon and admin pages. Opens results directly. */

const GROUP_LABELS = {
  articles: "Articles", students: "Students", lessons: "Lessons",
  library: "Library", media: "Media", lexicon: "Lexicon", pages: "Pages",
};

export default function GlobalSearch({ mobile = false }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const search = (value) => {
    setQ(value);
    clearTimeout(debounceRef.current);
    if (value.trim().length < 2) { setGroups([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await adminApi.globalSearch(value.trim());
        setGroups(res.groups || []);
        setOpen(true);
      } catch { /* silent */ }
      finally { setBusy(false); }
    }, 300);
  };

  const go = (url) => { setOpen(false); setQ(""); setGroups([]); navigate(url); };

  return (
    <div ref={boxRef} className={`relative ${mobile ? "w-full" : "w-56 xl:w-72"}`} data-testid="global-search">
      <div className="flex items-center gap-2 bg-white/[0.04] border border-white/10 focus-within:border-gold/50 px-3 py-2">
        {busy
          ? <Loader2 size={13} strokeWidth={1.5} className="text-gold animate-spin shrink-0" />
          : <Search size={13} strokeWidth={1.5} className="text-bone/40 shrink-0" />}
        <input
          type="text"
          value={q}
          onChange={(e) => search(e.target.value)}
          onFocus={() => { if (groups.length) setOpen(true); }}
          onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
          placeholder="Search ART, title, student…"
          data-testid="global-search-input"
          className="w-full bg-transparent outline-none text-[13px] text-bone placeholder:text-bone/30"
        />
      </div>
      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 max-h-[70vh] overflow-y-auto bg-[#0a0908] border border-gold/25 shadow-2xl z-[70]" data-testid="global-search-results">
          {groups.length === 0 && (
            <p className="px-4 py-3 text-[12px] text-bone/40" data-testid="global-search-empty">No results.</p>
          )}
          {groups.map((g) => (
            <div key={g.group} className="py-1">
              <p className="px-4 pt-2 pb-1 text-[9px] tracking-[0.28em] uppercase text-gold/60">
                {GROUP_LABELS[g.group] || g.group}
              </p>
              {g.items.map((it, i) => (
                <button key={`${g.group}-${i}`} onClick={() => go(it.url)}
                  data-testid={`search-result-${g.group}-${i}`}
                  className="w-full text-left px-4 py-2 hover:bg-gold/10 transition-colors">
                  <p className="text-[13px] text-bone truncate">{it.label}</p>
                  <p className="text-[10px] text-bone/40 truncate">{it.sublabel}</p>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
